import React, { useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL, vehicleStatusPresentation, leaseTypeLabel } from './fleet-ui.js';
import type { FleetVehicle } from './use-fleet-read-model.js';

type OpenVehicleHandler = Dispatch<SetStateAction<string | number | null>>;

interface VehicleRowProps {
  vehicle: FleetVehicle;
  onOpenVehicle: OpenVehicleHandler;
}

function VehicleRow({ vehicle, onOpenVehicle }: VehicleRowProps) {
  const status = vehicleStatusPresentation(vehicle.current_status || '');
  const hasIssues = (vehicle.issue_count || 0) > 0;

  return (
    <div
      onClick={() => onOpenVehicle(vehicle.id ?? null)}
      style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${hasIssues ? C.red : C.borderSoft}`, display: 'grid', gap: 8, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>#{vehicle.truck_number || '—'}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>
            {vehicle.year ? `${vehicle.year} ` : ''}{vehicle.make || ''} {vehicle.model || ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: status.color, background: status.background }}>
            {status.label}
          </span>
          {hasIssues && (
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: C.red, background: 'rgba(255,107,107,0.12)' }}>
              {vehicle.issue_count} issue{vehicle.issue_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
        <div style={{ color: C.textMuted, fontSize: 12 }}>Type: <span style={{ color: C.text }}>{leaseTypeLabel(vehicle.lease_type)}</span></div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>Terminal: <span style={{ color: C.text }}>{vehicle.terminal_name || vehicle.current_terminal_name || '—'}</span></div>
        {vehicle.vin ? <div style={{ color: C.textMuted, fontSize: 12 }}>VIN: <span style={{ color: C.text }}>{vehicle.vin}</span></div> : null}
        {vehicle.license_plate ? <div style={{ color: C.textMuted, fontSize: 12 }}>Plate: <span style={{ color: C.text }}>{vehicle.license_plate}</span></div> : null}
      </div>
    </div>
  );
}

interface FleetVehicleRegistryProps {
  vehicles: FleetVehicle[];
  search: string;
  onSearchChange: (value: string) => void;
  onOpenVehicle: OpenVehicleHandler;
}

export default function FleetVehicleRegistry({ vehicles, search, onSearchChange, onOpenVehicle }: FleetVehicleRegistryProps) {
  const [sortBy, setSortBy] = useState('truck_number');

  const filtered = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    const list = query
      ? vehicles.filter((v: FleetVehicle) => {
          const truckNum = String(v.truck_number || '').toLowerCase();
          const vin = String(v.vin || '').toLowerCase();
          const plate = String(v.license_plate || '').toLowerCase();
          const terminal = String(v.terminal_name || v.current_terminal_name || '').toLowerCase();
          return truckNum.includes(query) || vin.includes(query) || plate.includes(query) || terminal.includes(query);
        })
      : vehicles;

    return list.slice().sort((a: FleetVehicle, b: FleetVehicle) => {
      if (sortBy === 'issues') return (b.issue_count || 0) - (a.issue_count || 0);
      if (sortBy === 'status') return String(a.current_status || '').localeCompare(String(b.current_status || ''));
      return String(a.truck_number || '').localeCompare(String(b.truck_number || ''), undefined, { numeric: true });
    });
  }, [vehicles, search, sortBy]);

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
        Vehicle registry read surface
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 16 }}>
        <div>
          <label style={MUTED_LABEL}>Search</label>
          <input type="text" value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Truck #, VIN, plate, terminal..." style={CONTROL_INPUT} />
        </div>
        <div>
          <label style={MUTED_LABEL}>Sort by</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={CONTROL_INPUT}>
            <option value="truck_number">Truck #</option>
            <option value="status">Status</option>
            <option value="issues">Open issues</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: 12 }}>No vehicles match the current scope.</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map((v: FleetVehicle) => <VehicleRow key={v.id} vehicle={v} onOpenVehicle={onOpenVehicle} />)}
        </div>
      )}

      <div style={{ color: C.textMuted, fontSize: 11, marginTop: 12 }}>
        {filtered.length} of {vehicles.length} vehicles shown
      </div>
    </div>
  );
}
